import { Route, Routes } from 'react-router-dom';

import { ScrollToTop } from './components/common/ScrollToTop';
import { Layout } from './components/layout/Layout';
import { HomePage } from './pages/HomePage';
import { SearchResultsPage } from './pages/SearchResultsPage';
import { CaliberLandingPage } from './pages/CaliberLandingPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { RetailerDetailPage } from './pages/RetailerDetailPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { UserResponsibilityPage } from './pages/UserResponsibilityPage';
import { AboutPage } from './pages/AboutPage';
import { FaqPage } from './pages/FaqPage';
import { VsAmmoSeekPage } from './pages/VsAmmoSeekPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { ReviewQueuePage } from './pages/admin/ReviewQueuePage';
import { ReviewQueueItemPage } from './pages/admin/ReviewQueueItemPage';
import { UsersListPage } from './pages/admin/UsersListPage';
import { UserDetailPage } from './pages/admin/UserDetailPage';
import { AdvertisersListPage } from './pages/admin/AdvertisersListPage';
import { AdvertiserDetailPage } from './pages/admin/AdvertiserDetailPage';
import { ScrapeHealthPage } from './pages/admin/ScrapeHealthPage';
import { ScrapeErrorsPage } from './pages/admin/ScrapeErrorsPage';
import { AuditLogPage } from './pages/admin/AuditLogPage';
import { RetailersListPage } from './pages/admin/RetailersListPage';
import { ScrapeJobsPage } from './pages/admin/ScrapeJobsPage';
import { AlertPerformancePage } from './pages/admin/AlertPerformancePage';
import { AffiliatesListPage } from './pages/admin/AffiliatesListPage';
import { AffiliateDetailPage } from './pages/admin/AffiliateDetailPage';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { RegisterAdvertiserPage } from './pages/auth/RegisterAdvertiserPage';
import { DashboardLayout } from './pages/dashboard/DashboardLayout';
import { DashboardHomePage } from './pages/dashboard/DashboardHomePage';
import { AlertsPage } from './pages/dashboard/AlertsPage';
import { SavedSearchesPage } from './pages/dashboard/SavedSearchesPage';
import { WatchlistPage } from './pages/dashboard/WatchlistPage';
import { NotificationsPage } from './pages/dashboard/NotificationsPage';
import { PrivacyPage as DashboardPrivacyPage } from './pages/dashboard/PrivacyPage';
import { AccountPage } from './pages/dashboard/AccountPage';
import { AdvertiserLayout } from './pages/advertiser/AdvertiserLayout';
import { AdvertiserDashboardPage } from './pages/advertiser/AdvertiserDashboardPage';
import { CampaignsListPage } from './pages/advertiser/CampaignsListPage';
import { CampaignDetailPage } from './pages/advertiser/CampaignDetailPage';
import { AdvertiserAccountPage } from './pages/advertiser/AdvertiserAccountPage';
import { ReportsPage } from './pages/advertiser/ReportsPage';

/**
 * App shell. Routes match the public page list in spec.md §30 plus the
 * admin pages (Phase 1) and the dashboard / auth pages (Phase 4).
 *
 * - Public routes are wrapped in `Layout`.
 * - Admin routes are nested under `/admin` and protected by AdminLayout.
 * - User dashboard routes are nested under `/dashboard` and protected
 *   by `useAuthGuard` inside DashboardLayout (redirects to /login).
 */
function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route
          path="/"
          element={
            <Layout>
              <HomePage />
            </Layout>
          }
        />
        <Route
          path="/search"
          element={
            <Layout>
              <SearchResultsPage />
            </Layout>
          }
        />
        {/* #250: SEO-friendly slugged caliber URL → real indexable landing page
            (an unresolvable slug falls back to a keyword search, per #105). */}
        <Route
          path="/ammo/:caliberSlug"
          element={
            <Layout>
              <CaliberLandingPage />
            </Layout>
          }
        />
        <Route
          path="/products/:id"
          element={
            <Layout>
              <ProductDetailPage />
            </Layout>
          }
        />
        <Route
          path="/retailers/:id"
          element={
            <Layout>
              <RetailerDetailPage />
            </Layout>
          }
        />
        <Route
          path="/privacy"
          element={
            <Layout>
              <PrivacyPage />
            </Layout>
          }
        />
        <Route
          path="/terms"
          element={
            <Layout>
              <TermsPage />
            </Layout>
          }
        />
        <Route
          path="/responsibility"
          element={
            <Layout>
              <UserResponsibilityPage />
            </Layout>
          }
        />
        <Route
          path="/about"
          element={
            <Layout>
              <AboutPage />
            </Layout>
          }
        />
        <Route
          path="/faq"
          element={
            <Layout>
              <FaqPage />
            </Layout>
          }
        />
        <Route
          path="/roundrecon-vs-ammoseek"
          element={
            <Layout>
              <VsAmmoSeekPage />
            </Layout>
          }
        />

        {/* Auth pages — public, but wrapped in Layout so they get the
          skip link and header. */}
        <Route
          path="/login"
          element={
            <Layout>
              <LoginPage />
            </Layout>
          }
        />
        <Route
          path="/register"
          element={
            <Layout>
              <RegisterPage />
            </Layout>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <Layout>
              <ForgotPasswordPage />
            </Layout>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Layout>
              <ResetPasswordPage />
            </Layout>
          }
        />
        <Route
          path="/verify-email"
          element={
            <Layout>
              <VerifyEmailPage />
            </Layout>
          }
        />
        <Route
          path="/register-advertiser"
          element={
            <Layout>
              <RegisterAdvertiserPage />
            </Layout>
          }
        />

        {/* Advertiser dashboard — protected via AdvertiserLayout's useRoleGuard. */}
        <Route
          path="/dashboard/advertiser"
          element={
            <Layout>
              <AdvertiserLayout />
            </Layout>
          }
        >
          <Route index element={<AdvertiserDashboardPage />} />
          <Route path="campaigns" element={<CampaignsListPage />} />
          <Route path="campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="account" element={<AdvertiserAccountPage />} />
        </Route>

        {/* User dashboard — protected via DashboardLayout's useAuthGuard. */}
        <Route
          path="/dashboard"
          element={
            <Layout>
              <DashboardLayout />
            </Layout>
          }
        >
          <Route index element={<DashboardHomePage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="saved-searches" element={<SavedSearchesPage />} />
          <Route path="watchlist" element={<WatchlistPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="privacy" element={<DashboardPrivacyPage />} />
          <Route path="account" element={<AccountPage />} />
        </Route>

        {/* Admin section — protected via AdminLayout */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboardPage />} />
          <Route path="users" element={<UsersListPage />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          <Route path="advertisers" element={<AdvertisersListPage />} />
          <Route path="advertisers/:id" element={<AdvertiserDetailPage />} />
          <Route path="retailers" element={<RetailersListPage />} />
          <Route path="scrape-jobs" element={<ScrapeJobsPage />} />
          <Route path="alert-performance" element={<AlertPerformancePage />} />
          <Route path="review-queue" element={<ReviewQueuePage />} />
          <Route path="review-queue/:id" element={<ReviewQueueItemPage />} />
          <Route path="scrape-health" element={<ScrapeHealthPage />} />
          <Route path="scrape-errors" element={<ScrapeErrorsPage />} />
          <Route path="audit-log" element={<AuditLogPage />} />
          <Route path="affiliates" element={<AffiliatesListPage />} />
          <Route path="affiliates/:id" element={<AffiliateDetailPage />} />
        </Route>

        <Route
          path="*"
          element={
            <Layout>
              <NotFoundPage />
            </Layout>
          }
        />
      </Routes>
    </>
  );
}

export default App;
